import { injectable } from 'tsyringe';
import { ResponseError, type TaxonomyView } from '@bmai/context-api-client';
import { contextClient } from '@/shared/ContextApiClient';
import type { TaxonomyNodeInput, UsageTaxonomy, UsageTaxonomyNode } from './UsageCategoryTaxonomyTypes';

const USAGE_CATEGORY_DIMENSION_KEY = 'usageCategory';

/**
 * Reads and edits the tenant's usage-category taxonomy via the context service.
 * Maps the flat per-dimension taxonomy list into a category→subcategory tree and
 * exposes node CRUD in domain terms, so the generated client is touched in one
 * place. `createNode` re-resolves the new id (the create endpoint returns void).
 */
@injectable()
export class UsageCategoryTaxonomyService {
    private readonly dimensions = contextClient.dimensionsApi;
    private readonly taxonomies = contextClient.taxonomiesApi;

    async loadTaxonomy(): Promise<UsageTaxonomy> {
        return runApi('load usage categories', async () => {
            const dimensions = await this.dimensions.getDimensions();
            const dimension = dimensions.find((d) => d.key === USAGE_CATEGORY_DIMENSION_KEY) ?? null;
            if (!dimension) return { dimensionId: null, nodes: [] };
            const views = await this.dimensions.getByDimensionIdTaxonomies({ dimensionId: dimension.id });
            return { dimensionId: dimension.id, nodes: buildTree(views) };
        });
    }

    async createNode(dimensionId: string, input: TaxonomyNodeInput): Promise<string> {
        return runApi('create usage category', async () => {
            await this.dimensions.postByDimensionIdTaxonomies({
                dimensionId,
                createTaxonomyPayload: {
                    label: input.label,
                    parentTaxonomyId: input.parentTaxonomyId,
                    description: input.description,
                    enabled: input.enabled,
                    sortOrder: input.sortOrder,
                },
            });
            const views = await this.dimensions.getByDimensionIdTaxonomies({ dimensionId });
            const created = views.find(
                (v) => v.label === input.label && (v.parentTaxonomyId ?? null) === input.parentTaxonomyId && v.isActive,
            );
            if (!created) throw new Error(`Created usage category '${input.label}' could not be resolved`);
            return created.id;
        });
    }

    async updateNode(id: string, input: Partial<TaxonomyNodeInput>): Promise<void> {
        await runApi('update usage category', () =>
            this.taxonomies.putById({
                id,
                updateTaxonomyPayload: {
                    ...(input.label !== undefined ? { label: input.label } : {}),
                    ...(input.description !== undefined ? { description: input.description } : {}),
                    ...(input.enabled !== undefined ? { enabled: input.enabled } : {}),
                    ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
                    ...(input.parentTaxonomyId !== undefined ? { parentTaxonomyId: input.parentTaxonomyId } : {}),
                },
            }),
        );
    }

    async deleteNode(id: string): Promise<void> {
        await runApi('delete usage category', () => this.taxonomies.deleteById({ id }));
    }
}

function buildTree(views: TaxonomyView[]): UsageTaxonomyNode[] {
    const byId = new Map<string, UsageTaxonomyNode>();
    for (const view of views) {
        byId.set(view.id, {
            id: view.id,
            label: view.label,
            description: view.description,
            enabled: view.enabled,
            sortOrder: view.sortOrder,
            children: [],
        });
    }
    const roots: UsageTaxonomyNode[] = [];
    for (const view of views) {
        const node = byId.get(view.id);
        if (!node) continue;
        const parentId = view.parentTaxonomyId ?? null;
        const parent = parentId === null ? undefined : byId.get(parentId);
        if (parent) parent.children.push(node);
        else roots.push(node);
    }
    sortTree(roots);
    return roots;
}

function sortTree(nodes: UsageTaxonomyNode[]): void {
    nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
    for (const node of nodes) sortTree(node.children);
}

async function runApi<T>(op: string, call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (cause) {
        if (cause instanceof ResponseError) {
            throw new Error(await readError(cause.response, op));
        }
        throw cause;
    }
}

async function readError(response: Response, op: string): Promise<string> {
    const body = await response
        .clone()
        .json()
        .then((value) => value as { error?: string })
        .catch(() => null);
    if (body && typeof body.error === 'string' && body.error.length > 0) return body.error;
    return `${op} failed (${response.status} ${response.statusText})`;
}
